// Typed client for the Supabase Edge Functions (PRD §10).
import { supabase } from "./supabase";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function callFn<T>(
  name: string,
  body?: Record<string, unknown>,
  method: HttpMethod = "POST",
): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;

  const { data, error } = await supabase.functions.invoke<T>(name, {
    method,
    body,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (error) {
    // Surface the function's JSON error message if present.
    const message =
      (error as { context?: { error?: string } })?.context?.error ??
      error.message ??
      "Request failed";
    throw new Error(message);
  }
  return data as T;
}

export interface CaptionResult {
  caption: string;
  headline: string;
}

export const api = {
  generateCopy: (input: {
    topic: string;
    language: "ar" | "en";
    cohortIdx?: number[];
  }) => callFn<CaptionResult>("generate-copy", input),

  generateDesign: (input: {
    topic: string;
    language: "ar" | "en";
    headline: string;
    caption: string;
    cohortIdx?: number[];
    toggles: { doctorImage: boolean; phone: boolean; logo: boolean };
    pieceId?: string;
    isEdit?: boolean;
    editInstruction?: string;
  }) => callFn<{ jobId: string; pieceId: string }>("generate-design", input),

  generateCanvasDesign: (input: {
    topic: string;
    language: "ar" | "en";
    headline: string;
    caption: string;
    cohortIdx?: number[];
    toggles: { doctorImage: boolean; phone: boolean; logo: boolean };
    pieceId?: string;
  }) => callFn<{ jobId: string; pieceId: string }>("generate-canvas-design", input),

  editDesign: (input: {
    pieceId: string;
    editInstruction: string;
  }) => callFn<{ jobId: string; pieceId: string }>("edit-design", input),

  pollJob: (jobId: string) =>
    callFn<{
      status: "pending" | "done" | "failed";
      stage: string | null;
      result_url: string | null;
      error: string | null;
    }>(`poll-job?jobId=${encodeURIComponent(jobId)}`, undefined, "GET"),

  extractStyle: (input: { referenceUrls: string[] }) =>
    callFn<{ palette: string[]; mood: string; typography: string; layout: string }>(
      "extract-style",
      input,
    ),

  // Canvas assets (async jobs — poll via pollJob, then refetch the asset).
  generateCharacterSheet: () =>
    callFn<{ jobId: string }>("generate-character-sheet", {}),

  generateCanvasBackground: (input: { prompt: string }) =>
    callFn<{ jobId: string }>("generate-canvas-background", input),
};
